<template>
  <div>
    <b-button 
      :disabled="isExporting || disabled" 
      @click="handleExport" 
      :size="size" 
      :variant="variant"
      :class="buttonClass"
    >
      <b-spinner v-if="isExporting" small label="Exporting..."></b-spinner>
      {{ displayText }}
    </b-button>
  </div>
</template>

<script>
import NodeLockPannelService from '../../services/NodeLockPannelService.js';

export default {
  name: 'ExcelExportButton',
  props: {
    // Service method name for export (e.g., 'getAllNotBillContainerDash')
    serviceMethod: {
      type: String,
      required: true
    },
    // Export payload data
    exportData: {
      type: Object,
      default: function() { return {}; }
    },
    // Button text when not exporting
    buttonText: {
      type: String,
      default: 'Export As Excel'
    },
    // Button text when exporting
    exportingText: {
      type: String,
      default: 'Downloading Please Wait...'
    },
    // Button size
    size: {
      type: String,
      default: 'sm'
    },
    // Button variant
    variant: {
      type: String,
      default: 'warning'
    },
    // Additional CSS classes
    buttonClass: {
      type: String,
      default: ''
    },
    // Whether button is disabled
    disabled: {
      type: Boolean,
      default: false
    },
    // Custom headers for the request
    headers: {
      type: Object,
      default: function() { return {}; }
    },
    // Default filename for the downloaded file (fallback)
    filename: {
      type: String,
      default: 'export.xlsx'
    },
    // Default MIME type for the file (fallback)
    mimeType: {
      type: String,
      default: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
  },
  data() {
    return {
      isExporting: false
    }
  },
  methods: {
    handleExport() {
      if (this.isExporting) return;
      
      this.isExporting = true;
      
      // Check if the service method exists
      if (!NodeLockPannelService[this.serviceMethod]) {
        this.isExporting = false;
        this.$emit('export-error', new Error(`Service method '${this.serviceMethod}' not found`));
        return;
      }
      
      // Prepare the export payload
      const payload = Object.assign({}, this.exportData, {
        generateExcel: "1"
      });
      
      // Make the API call with blob response type
      const config = {
        responseType: 'blob',
        headers: Object.assign({
          'Content-Type': 'application/json'
        }, this.headers)
      };
      
      NodeLockPannelService[this.serviceMethod](payload, config)
        .then(function(response) {
          // Debug: Log the entire response object
          console.log("Full response object:", response);
          console.log("Response headers:", response.headers);
          console.log("Response data type:", typeof response.data);
          console.log("Response data:", response.data);
          
          // Handle the file download with dynamic filename and type detection
          this.downloadFile(response.data, response.headers);
          
          // Emit success event
          this.$emit('export-success', response);
        }.bind(this))
        .catch(function(error) {
          console.error('Export failed:', error);
          // Emit error event
          this.$emit('export-error', error);
        }.bind(this))
        .finally(function() {
          this.isExporting = false;
        }.bind(this));
    },
    
    downloadFile(blob, headers) {
      // Extract filename from Content-Disposition header if available
      let filename = this.filename; // Default fallback
      let mimeType = this.mimeType; // Default fallback
      
      // Debug: Log all available headers and blob info
      console.log("All headers received:", headers);
      console.log("Blob type:", blob.type);
      console.log("Blob size:", blob.size);
      
      // Try to get headers from different possible locations
      const responseHeaders = headers || {};
      const contentDisposition = responseHeaders['content-disposition'] || responseHeaders['Content-Disposition'];
      const contentType = responseHeaders['content-type'] || responseHeaders['Content-Type'];
      
      console.log("Content-Disposition:", contentDisposition);
      console.log("Content-Type:", contentType);
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        
        if (filenameMatch && filenameMatch[1]) {
          // Remove quotes if present
          filename = filenameMatch[1].replace(/['"]/g, '');
          console.log("Extracted filename from header:", filename);
        }
      }
      
      // Determine file type - prioritize blob.type, then headers, then filename extension
      if (blob.type && blob.type !== 'application/octet-stream') {
        mimeType = blob.type;
        console.log("Using blob.type:", mimeType);
      } else if (contentType) {
        mimeType = contentType;
        console.log("Using Content-Type header:", mimeType);
      } else if (filename) {
        const extension = filename.toLowerCase().split('.').pop();
        if (extension === 'zip') {
          mimeType = 'application/zip';
        } else if (extension === 'xlsx') {
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        } else if (extension === 'xls') {
          mimeType = 'application/vnd.ms-excel';
        }
        console.log("Using filename extension:", extension, "-> MIME type:", mimeType);
      }
      
      // Create blob with proper MIME type
      const blobWithType = new Blob([blob], { type: mimeType });
      
      // Create blob URL
      const url = window.URL.createObjectURL(blobWithType);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Log the final detected file type for debugging
      console.log(`Final result - Downloading file: ${filename} (${mimeType})`);
    }
  },
  
  computed: {
    displayText() {
      return this.isExporting ? this.exportingText : this.buttonText;
    }
  }
}
</script>

<style scoped>
/* Add any component-specific styles here */
</style>



// WEBPACK FOOTER //
// ExcelExportButton.vue?30e542b2